import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useQuery } from '@apollo/client'
import {
  Button,
  Heading,
  Grid,
  Placeholder,
  Tooltip,
} from '@enterprise-ui/canvas-ui-react'
import EnterpriseIcon, {
  FilterIcon,
  MinimizeIcon,
  MaximizeIcon,
  DownloadIcon,
  LinkIcon,
  ExportIcon,
} from '@enterprise-ui/icons'

import { useGridHeader } from './useGridHeader'
import { useGridDownload } from './useGridDownload'
import ClearItems from '../actionbar/ClearItems'
import { getUserInfo, userIsInSomeRole } from '../../../resources/commonUtills'
import { getSearchItemTypeCounts } from '../../../graphql/userSearch'

import constants from '../../../constants/constants'
import { SEARCH_ACTIONS } from '../../../constants/search'
import { useFirefly } from '../../../resources/useAnalytics'

export const GridHeader = (props) => {
  const { handleTrackEvent } = useFirefly()

  const { openCustomiseColumns, setOpenCustomiseColumns, handleCopyText } =
    useGridHeader()
  const {
    handleDownload,
    handleDownloadAllResults,
    handleMTADownload,
    downloadInProgress,
    downloadMtaInProgress,
  } = useGridDownload()
  const {
    openCloseToolPanel,
    getTotalResult,
    loading,
    setMaximize,
    maximize,
    gridApi,
    selectedItems,
    isInternalUserForBulk,
    onDownloadItemData,
  } = props

  const { filters, savedSearch } = useSelector(
    (state) => state.userSearchProfile,
  )

  const { refetch: refetchSearchItemTypeCounts } = useQuery(
    getSearchItemTypeCounts(),
    { skip: true, fetchPolicy: 'no-cache' },
  )

  const [downloadItemDataInProgress, setDownloadItemDataInProgress] =
    useState(false)

  const handleDownloadItemDataClick = async () => {
    const allFilters = [
      ...(filters?.stickerTypeId ?? []),
      ...(savedSearch?.activeSavedFilters ?? []),
    ]
    const searchText = filters?.searchText || ''
    setDownloadItemDataInProgress(true)
    try {
      const result = await refetchSearchItemTypeCounts({
        filters: allFilters,
        searchText,
      })
      const itemTypeCounts =
        result?.data?.getCatalogItemTypeCounts?.itemTypeCounts || []
      onDownloadItemData({ itemTypeCounts })
    } catch {
      onDownloadItemData({ itemTypeCounts: [] })
    } finally {
      setDownloadItemDataInProgress(false)
    }
  }

  const isHeaderChecked = useSelector(
    (state) => state.searchColumns.headerCheckbox,
  )
  const selectedGridItems = useSelector(
    (state) => state.searchColumns.selectedGridItems,
  )

  let selectedTcins = []
  if (gridApi.current?.api) {
    selectedTcins = selectedGridItems.map((filter) => filter.tcin).toString()
  }

  const canDownloadSelectedItems =
    isHeaderChecked ||
    (selectedGridItems.length > 0 &&
      selectedGridItems.length <= SEARCH_ACTIONS.MAX_SELECTABLE)
  const canDownloadAllResults =
    !loading &&
    getTotalResult > 0 &&
    getTotalResult <= SEARCH_ACTIONS.MAX_SELECTABLE

  const disableSelectionAwareDownloadButton =
    !canDownloadSelectedItems && !canDownloadAllResults
  const disableDownloadItemDataButton = !canDownloadAllResults

  const downloadItemDataTooltipContent = disableDownloadItemDataButton
    ? 'Please select the items'
    : 'Download item data (Max 10K)'

  const downloadMtaTooltipContent = disableSelectionAwareDownloadButton
    ? 'Please select the items'
    : 'Download results with MTA (Max 10K)'

  const getUserProfile = useSelector((state) => {
    return getUserInfo(state)
  })

  const isUserAllowedForMTADownload = userIsInSomeRole(getUserProfile?.roles, [
    constants.USER_ROLES.ADMIN,
    constants.USER_ROLES.SUPPORT,
    constants.USER_ROLES.MAINTAINER,
    constants.USER_ROLES.VENDOR,
    constants.USER_ROLES.VIEWER,
    constants.USER_ROLES.IAUVIEWER,
  ])

  return (
    <Grid.Container
      justify="space-between"
      className="grid-header hc-mt-none grid-header-info-container"
    >
      <Grid.Item xs={4} xl={8}>
        <Heading
          heading={6}
          className="hc-fs-md hc-lh-expanded font-color-default font-weight-700 grid-header-info-items"
        >
          {loading ? (
            <Placeholder.Text
              style={{ width: 120 }}
              className="hc-ml-dense hc-mr-dense"
            />
          ) : getTotalResult > 0 ? (
            <span>Result: ({getTotalResult.toLocaleString()})</span>
          ) : (
            <span>Results: No Items</span>
          )}
          {'  '}
          {selectedGridItems?.length !== 0 && (
            <span className="hc-fs-sm font-weight-100">
              <small className="divider-line-margin">|</small>{' '}
              {selectedGridItems.length} out of {getTotalResult} selected
            </span>
          )}
          {selectedGridItems?.length !== 0 && <ClearItems gridApi={gridApi} />}
        </Heading>
      </Grid.Item>
      <Grid.Item className="hc-mr-normal">
        {loading ? (
          <div style={{ display: 'flex' }}>
            <Placeholder.Text
              emphasized
              style={{ width: 20 }}
              className="hc-ml-dense hc-mr-normal"
            />
            <Placeholder.Text
              style={{ width: 20 }}
              className="hc-ml-dense hc-mr-normal"
            />
            <Placeholder.Text
              style={{ width: 20 }}
              className="hc-ml-dense hc-mr-normal"
            />
            <Placeholder.Text
              style={{ width: 20 }}
              className="hc-ml-dense hc-mr-dense"
            />
            <Placeholder.Text
              style={{ width: 150 }}
              className="hc-ml-dense hc-mr-dense"
            />
          </div>
        ) : getTotalResult > 0 ? (
          <div className="hc-mr-dense button-container">
            <Tooltip content="Customize table columns" location="top">
              <Button
                type="ghost"
                iconOnly
                className="bg-default-contrast-weak hc-mr-dense"
                onClick={() => {
                  setOpenCustomiseColumns((p) => !p)
                  openCloseToolPanel('columns', openCustomiseColumns)
                  handleTrackEvent(
                    'Search Header',
                    `Customize columns : ${openCustomiseColumns ? 'open' : 'close'}`,
                  )
                }}
                aria-label="View colunm filters"
              >
                <EnterpriseIcon icon={FilterIcon} />
              </Button>
            </Tooltip>
            <Tooltip
              content="Get links to this page (includes current filters)"
              location="top"
            >
              <Button
                type="ghost"
                iconOnly
                className="bg-default-contrast-weak hc-mr-dense"
                onClick={handleCopyText}
                aria-label="get link"
              >
                <EnterpriseIcon icon={LinkIcon} />
              </Button>
            </Tooltip>
            <Tooltip
              content={maximize ? 'Exit Fullscreen' : 'Fullscreen'}
              location="top"
            >
              <Button
                type="ghost"
                iconOnly
                className="bg-default-contrast-weak hc-mr-dense"
                onClick={() => {
                  setMaximize(!maximize)
                  handleTrackEvent(
                    'Search Header',
                    `${maximize ? 'Exit Fullscreen' : 'Enter Fullscreen'}`,
                  )
                }}
                aria-label="Minimize the table view"
              >
                {maximize ? (
                  <EnterpriseIcon icon={MinimizeIcon} />
                ) : (
                  <EnterpriseIcon icon={MaximizeIcon} />
                )}
              </Button>
            </Tooltip>

            <Tooltip
              content="Export visible columns | Max 10,000"
              location="top"
            >
              <Button
                type="ghost"
                iconOnly
                className="bg-default-contrast-weak hc-mr-dense"
                onClick={(e) => {
                  isInternalUserForBulk
                    ? handleDownloadAllResults(e, gridApi?.current?.api)
                    : handleDownload(e, gridApi?.current?.api)
                }}
                disabled={
                  isInternalUserForBulk
                    ? !canDownloadAllResults
                    : !canDownloadAllResults && selectedItems?.length === 0
                }
                isLoading={downloadInProgress}
                aria-label="Export visible columns"
              >
                <EnterpriseIcon icon={ExportIcon} />
              </Button>
            </Tooltip>
            {isInternalUserForBulk ? (
              <Tooltip content={downloadItemDataTooltipContent} location="top">
                <Button
                  type="secondary"
                  onClick={handleDownloadItemDataClick}
                  disabled={disableDownloadItemDataButton}
                  isLoading={downloadItemDataInProgress}
                >
                  <EnterpriseIcon icon={DownloadIcon} className="hc-mr-dense" />
                  Download item data
                </Button>
              </Tooltip>
            ) : (
              <Tooltip content={downloadMtaTooltipContent} location="top">
                <Button
                  type="secondary"
                  onClick={(e) => {
                    handleMTADownload(e, selectedTcins)
                  }}
                  disabled={
                    disableSelectionAwareDownloadButton ||
                    !isUserAllowedForMTADownload
                  }
                  isLoading={downloadMtaInProgress}
                >
                  Download with MTA
                </Button>
              </Tooltip>
            )}
          </div>
        ) : (
          <></>
        )}
      </Grid.Item>
    </Grid.Container>
  )
}
